import { TextInput } from '@mantine/core';
import { LoadingAnimation, Select, SelectableCard, useOidcContext } from '@uag/react-core';
import { ContractLanguageModel } from '@uag/welcome-shared/src/api/models';
import { useGetContractLanguages } from '@uag/welcome-shared/src/api/queries/contracts/contracts';
import { Card } from '@uag/welcome-shared/src/components/Card';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { z } from 'zod';

import selfSign from './assets/selfSign.svg';
import someoneElseSign from './assets/someoneElseSign.svg';

export const SignerType = {
    Self: 'Self',
    SomeoneElse: 'SomeoneElse',
};

export type SignerType = (typeof SignerType)[keyof typeof SignerType];

type Props = {
    selectedLanguage: string;
    onLanguageChange: (value: string | null | undefined) => void;
    signerEmail?: string;
    onSignerEmailChange: (value: string | undefined) => void;
    signerEmailError?: string;
    signerType: SignerType;
    onSignerTypeChange: (value: SignerType) => void;
};

export const getLanguageComboData = (languages: ContractLanguageModel[] | undefined) => {
    if (!languages) {
        return [];
    }

    return languages.map((language) => ({
        label: language.displayName,
        value: language.twoLetterLanguageCode,
    }));
};

const findDefaultLanguage = (locale: string, languages: ContractLanguageModel[]) => {
    const twoLetterLocale = locale.includes('-') ? locale.split('-')[0].toLowerCase() : locale.toLowerCase();
    return languages.find((language) => language.twoLetterLanguageCode.toLowerCase() === twoLetterLocale);
};

export const SelectSigner = ({
    signerType,
    onSignerTypeChange,
    onSignerEmailChange,
    selectedLanguage,
    onLanguageChange,
}: Props) => {
    const [signerEmailError, setSignerEmailError] = useState<string | undefined>();
    const [signerEmail, setSignerEmail] = useState<string | undefined>();
    const { user } = useOidcContext();

    const { requestId = '' } = useParams();
    const { t } = useTranslation();

    const { data: contractLanguages, isLoading: areContractLanguagesLoading } = useGetContractLanguages(requestId);

    useEffect(() => {
        if (!selectedLanguage && user?.locale && contractLanguages?.data && contractLanguages?.data.length !== 0) {
            const defaultLanguage = findDefaultLanguage(user.locale, contractLanguages.data);

            if (defaultLanguage) {
                onLanguageChange(defaultLanguage.twoLetterLanguageCode);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedLanguage, contractLanguages, user]);

    if (areContractLanguagesLoading) {
        return <LoadingAnimation />;
    }

    const handleSignerEmailChange = (value: string | undefined) => {
        setSignerEmail(value);
        setSignerEmailError(undefined);

        if (!z.email().safeParse(value).success) {
            onSignerEmailChange(undefined);
            setSignerEmailError(t('invalidEmail'));
            return;
        }
        onSignerEmailChange(value);
    };

    return (
        <div className="gap-base flex w-148 flex-col">
            <h4 className="text-text-ultra-light">{t('signUsageContract')}</h4>
            <div className="gap-lg flex flex-col">
                <Select
                    data={getLanguageComboData(contractLanguages?.data)}
                    description={t('contractLanguageDescription')}
                    label={t('contractLanguage')}
                    placeholder={t('selectContractLanguage')}
                    value={selectedLanguage}
                    required
                    onChange={onLanguageChange}
                />
                <SelectableCard.Group
                    className="w-full gap-6"
                    orientation="horizontal"
                    value={signerType}
                    wrap="nowrap"
                    onChange={onSignerTypeChange}
                >
                    <Card image={selfSign} imageClassName="h-40" text={t('iWillSignIt')} value={SignerType.Self} />
                    <Card
                        image={someoneElseSign}
                        imageClassName="h-40"
                        text={t('someoneElseWillSignIt')}
                        value={SignerType.SomeoneElse}
                    />
                </SelectableCard.Group>
                {signerType === SignerType.SomeoneElse && (
                    <TextInput
                        classNames={{ section: 'border-l-0' }}
                        error={signerEmailError}
                        label={t('signersEmail')}
                        placeholder={t('enterEmailPlaceholder')}
                        value={signerEmail}
                        required
                        onChange={(e) => handleSignerEmailChange(e.currentTarget.value)}
                    />
                )}
            </div>
        </div>
    );
};
